import { Col, ColProps, Row, RowProps } from "antd";
import React, {
  Children,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { CORETag } from "./CORETag";
import { CORETooltip } from "../Overlay/CORETooltip";
import classNames from "classnames";
import { spacing } from "../../shared/global";
import { TestID } from "../../shared/testids/testids";

export type CORETagListProps = {
  children: ReactNode;
  truncateAfter?: number;
  showTooltip?: boolean;
  testID: TestID;
  vertical?: boolean;
  autoTruncate?: boolean;
  /**
   * The width of the surrounding layout or parent container.
   * Used as a dependency to trigger recalculation when the layout changes.
   * The actual value is not used for calculation, only the change event.
   */
  layoutWidth?: number;
} & Pick<RowProps, "wrap" | "justify"> &
  Pick<ColProps, "span">;

const getStableKey = (child: ReactNode, index: number): string => {
  if (typeof child === "string" || typeof child === "number") {
    return `primitive-${child}`;
  }

  if (React.isValidElement(child)) {
    if (child.key != null) return String(child.key);

    const props = child.props as { label?: string };
    if (props.label) return `child-${props.label}`;
  }

  return `child-${index}`;
};

const EXTRA_FREE_SPACE = 4;

const TooltipContents: React.FC<{ hiddenChildren: ReactNode[] }> = ({
  hiddenChildren,
}) => (
  <Row gutter={[0, spacing.xxs]} justify="center" className="tag-list-vertical">
    {hiddenChildren.map((child, index) => (
      <Col key={getStableKey(child, index)}>{child}</Col>
    ))}
  </Row>
);

const TotalTags: React.FC<{
  hiddenChildren: ReactNode[];
  showTooltip?: boolean;
  testID: TestID;
}> = ({ hiddenChildren, showTooltip, testID }) => (
  <CORETooltip
    overlay
    hidden={!showTooltip}
    message={<TooltipContents hiddenChildren={hiddenChildren} />}
    testID={`${testID}/tags-tooltip`}
  >
    <CORETag label={`+${hiddenChildren.length}`} type="basic" />
  </CORETooltip>
);

type TagRowProps = {
  children: ReactNode;
  wrap: RowProps["wrap"];
  justify: RowProps["justify"];
  vertical: boolean;
  gutter?: [number, number];
};

const TagRow: React.FC<TagRowProps> = ({
  children,
  wrap,
  justify,
  vertical,
  gutter = [spacing.xxs, spacing.xxs],
}) => (
  <Row
    gutter={gutter}
    wrap={wrap}
    justify={justify}
    className={classNames("core-tag-list-tag-row", {
      "tag-list-vertical": vertical,
    })}
  >
    {children}
  </Row>
);

type TagListContentProps = {
  visibleChildren: ReactNode[];
  hiddenChildren: ReactNode[];
  span: ColProps["span"];
  showTooltip: boolean;
  testID: TestID;
};

const TagListContent: React.FC<TagListContentProps> = ({
  visibleChildren,
  hiddenChildren,
  span,
  showTooltip,
  testID,
}) => (
  <>
    {visibleChildren.map((child, index) => (
      <Col key={getStableKey(child, index)} span={span}>
        {child}
      </Col>
    ))}

    {hiddenChildren.length > 0 && (
      <Col span={span} className="total-tags">
        <TotalTags
          hiddenChildren={hiddenChildren}
          showTooltip={showTooltip}
          testID={testID}
        />
      </Col>
    )}
  </>
);

// ── Mode 1: No truncation – show all tags ──

type FullTagListProps = {
  arrayChildren: ReactNode[];
  rowProps: Pick<TagRowProps, "wrap" | "justify" | "vertical">;
  span: ColProps["span"];
};

const FullTagList: React.FC<FullTagListProps> = function FullTagList({
  arrayChildren,
  rowProps,
  span,
}) {
  return (
    <TagRow {...rowProps}>
      {arrayChildren.map((child, index) => (
        <Col key={getStableKey(child, index)} span={span}>
          {child}
        </Col>
      ))}
    </TagRow>
  );
};

// ── Mode 2: Fixed truncation (no DOM measurement) ──

type FixedTruncationTagListProps = {
  arrayChildren: ReactNode[];
  truncateAfter: number;
  rowProps: Pick<TagRowProps, "wrap" | "justify" | "vertical">;
  span: ColProps["span"];
  showTooltip: boolean;
  testID: TestID;
};

const FixedTruncationTagList: React.FC<FixedTruncationTagListProps> = ({
  arrayChildren,
  truncateAfter,
  rowProps,
  span,
  showTooltip,
  testID,
}) => {
  const visible = arrayChildren.slice(0, truncateAfter);
  const hidden = arrayChildren.slice(truncateAfter);

  return (
    <TagRow {...rowProps}>
      <TagListContent
        visibleChildren={visible}
        hiddenChildren={hidden}
        span={span}
        showTooltip={showTooltip}
        testID={testID}
      />
    </TagRow>
  );
};

// ── Mode 3: Auto-fit truncation (DOM measurement) ──

type AutoFitTagListProps = {
  arrayChildren: ReactNode[];
  truncateAfter?: number;
  rowProps: Pick<TagRowProps, "wrap" | "justify" | "vertical">;
  span: ColProps["span"];
  showTooltip: boolean;
  testID: TestID;
  layoutWidth?: number;
};

const AutoFitTagList: React.FC<AutoFitTagListProps> = ({
  arrayChildren,
  truncateAfter,
  rowProps,
  span,
  showTooltip,
  testID,
  layoutWidth,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const resizeRafRef = useRef<number | null>(null);
  const tagRefs = useRef<(HTMLDivElement | null)[]>([]);
  const totalTagRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState<number | undefined>(
    undefined
  );

  const calculateVisibleCount = useCallback(() => {
    if (!containerRef.current) return;

    const containerWidth = containerRef.current.offsetWidth;
    // A container that has not been laid out yet — or is inside something
    // hidden — measures zero, and truncating against that would hide every
    // tag. Leave the count alone and wait for a width worth measuring.
    if (containerWidth === 0) return;

    const totalTagWidth = totalTagRef.current?.offsetWidth ?? 0;
    const widthOf = (index: number) =>
      (tagRefs.current[index]?.offsetWidth ?? 0) + EXTRA_FREE_SPACE;

    const cap = Math.min(arrayChildren.length, truncateAfter ?? Infinity);

    // Checked before anything is reserved for the "+N" tag, because a row that
    // fits whole never renders one. Charging for it in every case is what made
    // a row that fits exactly drop its last tag — and the "+1" that appeared in
    // its place then filled the space the tag had been denied.
    if (cap === arrayChildren.length) {
      let widthOfAll = 0;
      for (let i = 0; i < arrayChildren.length; i++) widthOfAll += widthOf(i);
      if (widthOfAll <= containerWidth) {
        setVisibleCount(arrayChildren.length);
        return;
      }
    }

    let totalWidth = 0;
    let visibleCount = 0;

    for (let i = 0; i < cap; i++) {
      if (totalWidth + widthOf(i) + totalTagWidth > containerWidth) break;

      totalWidth += widthOf(i);
      visibleCount++;
    }

    setVisibleCount(visibleCount);
  }, [arrayChildren, truncateAfter, layoutWidth]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Use rAF to ensure DOM has been painted before measuring
    const rafId = requestAnimationFrame(calculateVisibleCount);

    // Re-measuring waits a frame of its own: a container that has just been
    // resized — or, on the chart full screen overlay, just been moved into the
    // document — has not been laid out at its final width by the time the
    // change is announced, and measuring it there is what leaves the list
    // truncated to a width it no longer has.
    const remeasure = () => {
      if (resizeRafRef.current !== null) {
        cancelAnimationFrame(resizeRafRef.current);
      }
      resizeRafRef.current = requestAnimationFrame(calculateVisibleCount);
    };

    // The viewport changing, which is also how `useChartFullScreen` announces
    // its overlay to everything that sizes itself from the window.
    window.addEventListener("resize", remeasure);

    // Everything the window cannot see: a sider opening beside the list, a
    // dashboard tile dragged wider, a panel collapsing. Each changes the room
    // available without the viewport changing at all.
    //
    // Safe only while this element takes its width from its parent. Give it a
    // shrink-to-fit ancestor and its width becomes a function of the tag count
    // measured here, and each hidden tag narrows it enough to hide the next.
    let lastWidth = containerRef.current?.offsetWidth ?? 0;
    const observer = new ResizeObserver(() => {
      const width = containerRef.current?.offsetWidth ?? 0;
      // Only width decides how many tags fit. Height changes as the row wraps,
      // which is a result of the count measured here rather than a reason to
      // measure again — reacting to it would feed this observer its own output.
      if (width === lastWidth) return;
      lastWidth = width;
      remeasure();
    });
    if (containerRef.current) observer.observe(containerRef.current);

    return () => {
      cancelAnimationFrame(rafId);
      if (resizeRafRef.current !== null)
        cancelAnimationFrame(resizeRafRef.current);
      window.removeEventListener("resize", remeasure);
      observer.disconnect();
    };
  }, [calculateVisibleCount]);

  const effectiveCount = visibleCount ?? arrayChildren.length;
  const autoVisible = arrayChildren.slice(0, effectiveCount);
  const autoHidden = arrayChildren.slice(effectiveCount);

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      {/* Hidden measurement layer */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          visibility: "hidden",
          zIndex: -1,
          whiteSpace: "nowrap",
          pointerEvents: "none",
        }}
      >
        {arrayChildren.map((child, i) => (
          <div
            key={getStableKey(child, i)}
            ref={(el) => {
              tagRefs.current[i] = el;
            }}
            style={{ display: "inline-block", marginRight: spacing.xxs }}
          >
            {child}
          </div>
        ))}

        <div
          ref={totalTagRef}
          style={{ display: "inline-block", marginRight: spacing.xxs }}
        >
          <CORETag label={`+${arrayChildren.length}`} type="basic" />
        </div>
      </div>

      <TagRow {...rowProps}>
        <TagListContent
          visibleChildren={autoVisible}
          hiddenChildren={autoHidden}
          span={span}
          showTooltip={showTooltip}
          testID={testID}
        />
      </TagRow>
    </div>
  );
};

// ── Main CORETagList dispatcher ──

export const CORETagList = React.memo(function CORETagList({
  children,
  truncateAfter,
  wrap = false,
  justify = "start",
  vertical = false,
  showTooltip = false,
  span,
  testID,
  autoTruncate = false,
  layoutWidth,
}: CORETagListProps) {
  const arrayChildren = useMemo(() => Children.toArray(children), [children]);
  const rowProps = { wrap, justify, vertical } as const;

  if (truncateAfter === undefined && !autoTruncate) {
    return (
      <FullTagList
        arrayChildren={arrayChildren}
        rowProps={rowProps}
        span={span}
      />
    );
  }

  if (!autoTruncate) {
    return (
      <FixedTruncationTagList
        arrayChildren={arrayChildren}
        truncateAfter={truncateAfter!}
        rowProps={rowProps}
        span={span}
        showTooltip={showTooltip}
        testID={testID}
      />
    );
  }

  return (
    <AutoFitTagList
      arrayChildren={arrayChildren}
      truncateAfter={truncateAfter}
      rowProps={rowProps}
      span={span}
      showTooltip={showTooltip}
      testID={testID}
      layoutWidth={layoutWidth}
    />
  );
});
